import { Injectable, inject } from '@angular/core';
import { TrialsFacade, redeemTrialSuccess, updateConnectionState } from '@app/+state';
import { GainsightService } from '@app/+state/gainsight/gainsight.service';
import { CommonConstants } from '@core/common-constants';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { TrialLinkStatus } from '@lemans/shared';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { userLoginComplete } from '../control-page/control-page.actions';

import { ApplicationCardButtonAction } from '../lemans-app.model';

import { HubConnectionState } from '@ra-state';
import { DataService } from '@servicesV2/data.service';
import { productToString } from '../trials/trials.model';
import {
  UserDataFacade,
  applyCustomProvisioningEntitlement,
  applyRegularEntitlement,
  cardClicked,
  getUserPreferencesSuccess,
  setTenantDetails,
  setUnallocatedEntitlementsCount,
} from '../user-data';
import { resetSession } from './gainsight.actions';
import { GSEventName, GSOrgContext, GSUserContext } from './gainsight.types';

@Injectable({
  providedIn: 'root',
})
export class GainsightEffects {
  private readonly actions$ = inject(Actions);
  private readonly gainsightService = inject(GainsightService);
  private readonly userDataFacade = inject(UserDataFacade);
  private readonly trialsFacade = inject(TrialsFacade);
  private readonly dataService = inject(DataService);

  pushUserProfile$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(userLoginComplete),
        map((payload) => {
          const userProfile = payload.payload;
          const userIdentity: GSUserContext = {
            email: userProfile.email,
            id: userProfile.userId,
            domain: CommonConstants.getEmailDomain(userProfile.email),
            raBusinessPartnerID: CommonConstants.cleanLeftTrailingZeros(userProfile.raBusinessPartnerID),
            company: userProfile.company,
          };
          this.gainsightService.pushUserProfile(userIdentity);
        }),
      );
    },
    { dispatch: false },
  );

  resetSession$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(resetSession),
        map(() => {
          this.gainsightService.reset();
        }),
      );
    },
    { dispatch: false },
  );

  setOrganizationContext$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(setTenantDetails),
        map((payload) => {
          const context: GSOrgContext = {
            organizationId: String(payload.payload?.id),
            organizationName: String(payload.payload?.name),
          };
          this.gainsightService.setGlobalContext(context);
        }),
      );
    },
    { dispatch: false },
  );

  pushEntitlementAppliedEvent$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(applyRegularEntitlement, applyCustomProvisioningEntitlement),
        map((payload) => {
          const { domain, payload: quantity } = payload;
          const { available, catalogNumber, name, serviceKind, status } = payload.entitlementData.entitlement;
          const customEvent = {
            eventName: GSEventName.EntitlementApplied,
            eventPayload: { available, catalogNumber, name, serviceKind, status, domain, quantity },
          };
          this.gainsightService.pushCustomEvent(customEvent);
        }),
      );
    },
    { dispatch: false },
  );

  pushSignalrDisconnected$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(updateConnectionState),
        concatLatestFrom(() => this.userDataFacade.userID$),
        map(([payload, userId]) => {
          if (payload.payload === HubConnectionState.Disconnected) {
            const customEvent = {
              eventName: GSEventName.SignalRDisconnectedPopup,
              eventPayload: { userId },
            };
            this.gainsightService.pushCustomEvent(customEvent);
          }
        }),
      );
    },
    { dispatch: false },
  );

  pushUsersActiveEntitlements$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getUserPreferencesSuccess),
      concatLatestFrom(() => this.userDataFacade.userID$),
      switchMap(([_payload, userId]) => {
        return this.dataService.getUnAllocatedEntitlements$(String(userId));
      }),
      switchMap((payload) => {
        const customEvent = {
          eventName: GSEventName.PostLoginUnallocatedEntitlements,
          eventPayload: {
            count: payload.totalEntitlementCount,
          },
        };
        this.gainsightService.pushCustomEvent(customEvent);
        return [setUnallocatedEntitlementsCount({ payload })];
      }),
    );
  });

  cardClicked$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(cardClicked),
        map((payload) => {
          switch (payload.action) {
            case ApplicationCardButtonAction.OPEN:
              this.gainsightService.pushCustomEvent({
                eventName: GSEventName.ApplicationCard,
                eventPayload: { applicationId: payload.card.appName, applicationName: payload.card.appId },
              });
              break;
            case ApplicationCardButtonAction.PRODUCT_INFO:
              this.gainsightService.pushCustomEvent({
                eventName: GSEventName.ApplicationCardInfo,
                eventPayload: { applicationId: payload.card.appName, applicationName: payload.card.appId },
              });
              break;
            default:
              break;
          }
        }),
      );
    },
    { dispatch: false },
  );

  trialRedeemed$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(redeemTrialSuccess),
        concatLatestFrom(() => this.trialsFacade.trialBundles$),
        map(([trialRedeemed, trialBundles]) => {
          const bundleId = trialRedeemed.payload.trialReservationDetails.campaignBundleId;
          const trialBundle = trialBundles.find((el) => el.id === bundleId);
          if (!trialBundle) {
            return;
          }
          const customEvent = {
            eventName: GSEventName.BundleTrialRedeemed,
            eventPayload: {
              trialReservationId: trialRedeemed.payload.trialReservationId,
              id: trialRedeemed.payload.trialReservationDetails.id,
              campaignId: trialRedeemed.payload.trialReservationDetails.campaignId,
              status: TrialLinkStatus.REDEEMED,
              campaignBundleId: trialRedeemed.payload.trialReservationDetails.campaignBundleId,
              durationInDays: trialRedeemed.payload.trialReservationDetails.durationInDays,
              expiresOn: trialRedeemed.payload.trialReservationDetails.expiresOn,
              trialBundleName: trialBundle.name,
              credits: trialBundle.credits ? 'With credits' : 'Without credits',
              products: trialBundle.products.map(productToString).join(', '),
            },
          };
          this.gainsightService.pushCustomEvent(customEvent);
        }),
      );
    },
    { dispatch: false },
  );
}
